import React from 'react';
import { Image, Pressable, StyleSheet, Text, View, type StyleProp, type ViewStyle } from 'react-native';
import { Gradient } from './Gradient';
import { money, statusOf, stepIndex, tokens, tone, type Tone } from './commerceTokens';

/**
 * The customer side of buying - wallet, canteen menu, cart, orders - drawn the same in every app
 * (owner, 19 Oct 2026: "should have common designs"). Plain components: no navigation, no sign-in,
 * no API. Each app passes data and callbacks; the Student app will use these as they are.
 */

const T = tokens;

// ── Header ───────────────────────────────────────────────────────────────────

export function HeroHeader({ title, subtitle, colors = T.hero, children }: {
  title: string; subtitle?: string; colors?: string[]; children?: React.ReactNode;
}) {
  return (
    <Gradient colors={colors} style={s.hero} testID="commerce-hero">
      <View style={s.heroBlob} />
      <Text style={s.heroTitle}>{title}</Text>
      {!!subtitle && <Text style={s.heroSub}>{subtitle}</Text>}
      {children}
    </Gradient>
  );
}

/** Pills on the header (children, days) or on the page (meal slots). */
export function Chips<V extends string>({ options, value, onChange, onHero = false, accent = T.tones.teal.c, testID }: {
  options: { value: V; label: string }[]; value: V | null; onChange: (v: V) => void;
  onHero?: boolean; accent?: string; testID?: string;
}) {
  return (
    <View style={s.chips} testID={testID}>
      {options.map(o => {
        const on = o.value === value;
        return (
          <Pressable key={o.value} onPress={() => onChange(o.value)} accessibilityRole="button" accessibilityState={{ selected: on }}
            style={[s.chip, onHero ? (on ? s.chipHeroOn : s.chipHero) : (on ? { backgroundColor: accent, borderColor: accent } : s.chipPage)]}
            testID={testID ? `${testID}-${o.value}` : undefined}>
            <Text style={[s.chipText, onHero ? (on ? { color: T.hero[0], fontWeight: '800' } : { color: '#FFFFFF' }) : (on ? { color: '#FFFFFF', fontWeight: '800' } : { color: T.ink })]}>{o.label}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}

// ── Money ────────────────────────────────────────────────────────────────────

export function BalanceCard({ label, amount, currency, meta, actions, colors = [T.tones.teal.c, T.tones.teal.c2], testID }: {
  label: string; amount: number; currency: string; meta?: string;
  actions?: { label: string; onPress: () => void; primary?: boolean; testID?: string }[];
  colors?: string[]; testID?: string;
}) {
  return (
    <Gradient colors={colors} style={s.balance} testID={testID}>
      <Text style={s.balanceMeta}>{label}</Text>
      <Text style={s.balanceAmount}>{money(amount, currency)}</Text>
      {!!meta && <Text style={s.balanceMeta}>{meta}</Text>}
      {!!actions?.length && (
        <View style={s.row8}>
          {actions.map(a => (
            <Pressable key={a.label} onPress={a.onPress} style={[s.balanceBtn, a.primary ? s.balanceBtnPrimary : s.balanceBtnGhost]} testID={a.testID}>
              <Text style={[s.balanceBtnText, a.primary ? { color: colors[0] } : { color: '#FFFFFF' }]}>{a.label}</Text>
            </Pressable>
          ))}
        </View>
      )}
    </Gradient>
  );
}

/** Fees plus wallet bills, for the home screen. */
export function PendingBanner({ total, breakdown, currency, overdue, onPay }: {
  total: number; breakdown: string; currency: string; overdue: boolean; onPay: () => void;
}) {
  if (!(total > 0)) return null;
  return (
    <View style={[s.banner, overdue && { backgroundColor: T.tones.pink.tint, borderColor: T.tones.pink.edge }]} testID="pending-amount-banner">
      <View style={{ flex: 1 }}>
        <View style={s.bannerTitleRow}>
          <Text style={[s.bannerMark, overdue && { color: T.danger }]}>!</Text>
          <Text style={[s.bannerTitle, overdue && { color: T.danger }]}>Fees Amount Pending: {money(total, currency)}</Text>
        </View>
        <Text style={s.muted}>{breakdown}</Text>
      </View>
      <Pressable onPress={onPay} style={s.bannerPay} testID="pending-amount-pay"><Text style={s.bannerPayText}>Pay now</Text></Pressable>
    </View>
  );
}

/** A big coloured action, e.g. "Order food" on the home screen. */
export function ActionButton({ label, onPress, tone: t = 'orange', disabled, testID, style }: {
  label: string; onPress: () => void; tone?: Tone; disabled?: boolean; testID?: string; style?: StyleProp<ViewStyle>;
}) {
  const c = tone(t);
  // Disabled reads as disabled: a quiet grey with dark text. The tone faded to half left white
  // text on a pale fill that could hardly be read (owner, 2 Nov 2026).
  const fill = disabled ? [T.line, T.line] : [c.c, c.c2];
  return (
    <Pressable onPress={onPress} disabled={disabled} accessibilityRole="button" accessibilityState={{ disabled: !!disabled }}
      testID={testID} style={[s.actionPress, style]}>
      <Gradient colors={fill} style={s.action}>
        <Text style={[s.actionText, disabled && { color: T.muted }]} numberOfLines={1}>{label}</Text>
      </Gradient>
    </Pressable>
  );
}

// ── Menu ─────────────────────────────────────────────────────────────────────

export function MenuItemRow({ name, price, currency, isVeg, imageUri, quantity, onAdd, onRemove, tone: t = 'orange', testID }: {
  name: string; price: number; currency: string; isVeg?: boolean | null; imageUri?: string | null;
  quantity: number; onAdd: () => void; onRemove: () => void; tone?: Tone; testID?: string;
}) {
  const c = tone(t);
  const [failed, setFailed] = React.useState(false);
  return (
    <View style={s.item} testID={testID}>
      {imageUri && !failed
        ? <Image source={{ uri: imageUri }} style={s.itemImg} onError={() => setFailed(true)} />
        : <View style={[s.itemImg, { backgroundColor: c.tint, alignItems: 'center', justifyContent: 'center' }]}><Text style={{ fontSize: 18, color: c.c, fontWeight: '800' }}>{name.slice(0, 1)}</Text></View>}
      <View style={{ flex: 1 }}>
        <View style={s.row6}>
          {isVeg !== null && isVeg !== undefined && (
            <View style={[s.veg, { borderColor: isVeg ? '#16A34A' : '#B91C1C' }]} accessibilityLabel={isVeg ? 'Vegetarian' : 'Not vegetarian'}>
              <View style={[s.vegDot, { backgroundColor: isVeg ? '#16A34A' : '#B91C1C' }]} />
            </View>
          )}
          <Text style={s.itemName} numberOfLines={2}>{name}</Text>
        </View>
        <Text style={s.muted}>{money(price, currency)}</Text>
      </View>
      {quantity === 0 ? (
        <Pressable onPress={onAdd} accessibilityLabel={`Add ${name}`} testID={testID ? `${testID}-add` : undefined}>
          <Gradient colors={[c.c, c.c2]} style={s.addBtn}><Text style={s.addText}>+</Text></Gradient>
        </Pressable>
      ) : (
        <View style={s.row8}>
          <Pressable onPress={onRemove} style={[s.qtyBtn, { backgroundColor: c.tint }]} accessibilityLabel={`One less ${name}`} testID={testID ? `${testID}-less` : undefined}>
            <Text style={[s.qtyBtnText, { color: c.c }]}>−</Text>
          </Pressable>
          <Text style={s.qty} testID={testID ? `${testID}-qty` : undefined}>{quantity}</Text>
          <Pressable onPress={onAdd} style={[s.qtyBtn, { backgroundColor: c.tint }]} accessibilityLabel={`One more ${name}`} testID={testID ? `${testID}-more` : undefined}>
            <Text style={[s.qtyBtnText, { color: c.c }]}>+</Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}

/** The dark bar at the bottom of the menu: how many, how much, and the next step. */
export function CartBar({ count, total, currency, subtitle, actionLabel, onPress, tone: t = 'green', disabled, testID = 'commerce-cart-bar' }: {
  count: number; total: number; currency: string; subtitle?: string; actionLabel: string;
  onPress: () => void; tone?: Tone; disabled?: boolean; testID?: string;
}) {
  if (count <= 0) return null;
  const c = tone(t);
  return (
    <View style={s.cartBar} testID={testID}>
      <View style={{ flex: 1 }}>
        <Text style={s.cartSub}>{count} {count === 1 ? 'item' : 'items'}{subtitle ? ` · ${subtitle}` : ''}</Text>
        <Text style={s.cartTotal}>{money(total, currency)}</Text>
      </View>
      <Pressable onPress={onPress} disabled={disabled} accessibilityRole="button" testID={`${testID}-action`} style={{ opacity: disabled ? 0.5 : 1 }}>
        <Gradient colors={[c.c, c.c2]} style={s.cartBtn}><Text style={s.actionText}>{actionLabel}</Text></Gradient>
      </Pressable>
    </View>
  );
}

/** Who pays, with the balance before and after - or how short the wallet is. */
export function PayFromCard({ title, balance, total, currency, selected = true, onPress, testID }: {
  title: string; balance?: number | null; total: number; currency: string; selected?: boolean; onPress?: () => void; testID?: string;
}) {
  const hasBalance = balance !== null && balance !== undefined;
  const short = hasBalance && balance! < total;
  return (
    <Pressable onPress={onPress} disabled={!onPress} testID={testID}
      style={[s.payFrom, selected ? { borderColor: T.tones.teal.c, backgroundColor: T.tones.teal.tint } : null, short ? { borderColor: T.danger, backgroundColor: T.tones.pink.tint } : null]}>
      <Text style={s.itemName}>{title}</Text>
      <Text style={[s.muted, short && { color: T.danger, fontWeight: '700' }]}>
        {!hasBalance ? 'Cash or card when you collect'
          : short ? `${money(balance!, currency)} - ${money(total - balance!, currency)} short. Top up first.`
          : `${money(balance!, currency)} → ${money(balance! - total, currency)} after this order`}
      </Text>
    </Pressable>
  );
}

// ── Orders ───────────────────────────────────────────────────────────────────

export function StatusPill({ orderStatus }: { orderStatus: string }) {
  const st = T.status[statusOf(orderStatus)];
  return <View style={[s.pill, { backgroundColor: st.bg }]}><Text style={[s.pillText, { color: st.fg }]}>{st.label}</Text></View>;
}

/** Placed -> Preparing -> Ready -> Collected, with the current step lit. */
export function OrderTracker({ orderStatus }: { orderStatus: string }) {
  const at = stepIndex(statusOf(orderStatus));
  if (at < 0) return null;
  return (
    <View style={s.steps} accessibilityLabel={`Order is ${T.steps[at]}`}>
      {T.steps.map((label, i) => {
        const done = i < at || (i === at && at === T.steps.length - 1);
        const now = i === at && !done;
        return (
          <View key={label} style={s.step}>
            <View style={[s.stepDot, done && { backgroundColor: T.tones.green.c }, now && { backgroundColor: T.tones.blue.c, borderWidth: 3, borderColor: T.tones.blue.tint }]} />
            <Text style={[s.stepText, done && { color: '#15803D' }, now && { color: T.tones.blue.c, fontWeight: '800' }]}>{label}</Text>
          </View>
        );
      })}
    </View>
  );
}

export function OrderCard({ title, orderStatus, lines, footer, total, currency, showTracker = true, testID }: {
  title: string; orderStatus: string; lines: string; footer?: string; total: number; currency: string; showTracker?: boolean; testID?: string;
}) {
  return (
    <View style={s.card} testID={testID}>
      <View style={s.rowBetween}>
        <Text style={s.itemName}>{title}</Text>
        <StatusPill orderStatus={orderStatus} />
      </View>
      <Text style={s.muted}>{lines} · {money(total, currency)}</Text>
      {!!footer && <Text style={s.muted}>{footer}</Text>}
      {showTracker && <OrderTracker orderStatus={orderStatus} />}
    </View>
  );
}

export function Card({ children, style, testID }: { children: React.ReactNode; style?: StyleProp<ViewStyle>; testID?: string }) {
  return <View style={[s.card, style]} testID={testID}>{children}</View>;
}

export const commerceStyles = StyleSheet.create({
  ground: { backgroundColor: T.ground },
  title: { fontSize: 15, fontWeight: '800', color: T.ink },
  muted: { fontSize: 12, color: T.muted },
  line: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 7, borderBottomWidth: 1, borderBottomColor: '#EEF1F6' },
});

const s = StyleSheet.create({
  hero: { paddingHorizontal: 16, paddingTop: 16, paddingBottom: 14, borderRadius: 18, overflow: 'hidden' },
  heroBlob: { position: 'absolute', right: -40, top: -40, width: 140, height: 140, borderRadius: 70, backgroundColor: 'rgba(237,179,34,0.28)' },
  heroTitle: { color: '#FFFFFF', fontSize: 18, fontWeight: '800' },
  heroSub: { color: 'rgba(255,255,255,0.9)', fontSize: 12.5, marginTop: 2 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: T.radius.chip, borderWidth: 1 },
  chipHero: { backgroundColor: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.25)' },
  chipHeroOn: { backgroundColor: '#FFFFFF', borderColor: '#FFFFFF' },
  chipPage: { backgroundColor: T.card, borderColor: T.line },
  chipText: { fontSize: 12.5 },
  balance: { borderRadius: T.radius.card, padding: 14 },
  balanceMeta: { color: 'rgba(255,255,255,0.92)', fontSize: 11.5 },
  balanceAmount: { color: '#FFFFFF', fontSize: 28, fontWeight: '800', marginVertical: 2 },
  balanceBtn: { flex: 1, alignItems: 'center', paddingVertical: 9, borderRadius: T.radius.button },
  balanceBtnPrimary: { backgroundColor: '#FFFFFF' },
  balanceBtnGhost: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.6)' },
  balanceBtnText: { fontWeight: '800', fontSize: 13 },
  banner: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: T.radius.tile, backgroundColor: T.warnBg, borderWidth: 1, borderColor: T.warnEdge },
  bannerTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  bannerMark: { fontSize: 16, fontWeight: '800', color: T.warnInk },
  bannerTitle: { fontSize: 14.5, fontWeight: '800', color: T.warnInk },
  bannerPay: { backgroundColor: T.hero[0], borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8 },
  bannerPayText: { color: '#FFFFFF', fontWeight: '800', fontSize: 12.5 },
  // flex: 1 spelled out, so a screen's own flexGrow/flexShrink/flexBasis can replace each part; on
  // the web the `flex` shorthand won over them and squeezed "Review weekly order" (owner, 2 Nov 2026).
  actionPress: { flexGrow: 1, flexShrink: 1, flexBasis: 0 },
  // Padding on all sides, inside the colour: a label never runs into the button's edges.
  action: { borderRadius: T.radius.button, paddingVertical: 12, paddingHorizontal: 20, alignItems: 'center', justifyContent: 'center' },
  actionText: { color: '#FFFFFF', fontWeight: '800', fontSize: 13.5 },
  item: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: T.card, borderWidth: 1, borderColor: T.line, borderRadius: T.radius.tile, padding: 9 },
  itemImg: { width: 50, height: 50, borderRadius: 10 },
  itemName: { fontSize: 14, fontWeight: '800', color: T.ink, flexShrink: 1 },
  veg: { width: 12, height: 12, borderWidth: 1.5, borderRadius: 2, alignItems: 'center', justifyContent: 'center' },
  vegDot: { width: 5, height: 5, borderRadius: 3 },
  addBtn: { width: 32, height: 32, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  addText: { color: '#FFFFFF', fontSize: 18, fontWeight: '800', marginTop: -1 },
  qtyBtn: { width: 28, height: 28, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  qtyBtnText: { fontSize: 16, fontWeight: '800' },
  qty: { fontSize: 14, fontWeight: '800', color: T.ink, minWidth: 14, textAlign: 'center' },
  cartBar: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: T.ink, borderRadius: T.radius.card, paddingHorizontal: 14, paddingVertical: 10, margin: 12 },
  cartSub: { color: '#C9D1E8', fontSize: 11.5 },
  cartTotal: { color: '#FFFFFF', fontSize: 16, fontWeight: '800' },
  cartBtn: { borderRadius: T.radius.button, paddingHorizontal: 16, paddingVertical: 10 },
  payFrom: { borderWidth: 2, borderColor: T.line, borderRadius: T.radius.tile, padding: 10, backgroundColor: T.card, gap: 2 },
  pill: { borderRadius: 999, paddingHorizontal: 8, paddingVertical: 3 },
  pillText: { fontSize: 11, fontWeight: '800' },
  steps: { flexDirection: 'row', marginTop: 10 },
  step: { flex: 1, alignItems: 'center', gap: 4 },
  stepDot: { width: 16, height: 16, borderRadius: 8, backgroundColor: T.line },
  stepText: { fontSize: 10.5, color: '#9AA3B8' },
  card: { backgroundColor: T.card, borderRadius: T.radius.card, borderWidth: 1, borderColor: T.line, padding: 12, gap: 4 },
  row6: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  row8: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 },
  rowBetween: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  muted: { fontSize: 12, color: T.muted },
});
